import { useState } from 'react'
import { api, type OutreachGroup, type PoolPlatform } from '@/api/client'
import { checkTemplate, useCreatorMutation } from '@/lib/creators'
import { Button, Drawer, Field, Input, errorText, useToast } from '@/ui'
import { GroupFields, type GroupFieldsValue } from './parts'

/** One group's message approach, editable after the outreach exists.
 *  Saving keeps the drafts already written; 다시 쓰기 writes the ones still
 *  waiting for approval again (approved and sent ones are never touched). */
export function GroupDrawer({
  outreachId,
  group,
  language,
  platform,
  onClose,
}: {
  outreachId: number
  group: OutreachGroup
  language: string
  platform: PoolPlatform
  onClose: () => void
}) {
  const toast = useToast()
  const [label, setLabel] = useState(group.label)
  const [v, setV] = useState<GroupFieldsValue>({
    offer: group.offer,
    angle: group.angle,
    tone: group.tone,
    template: group.template,
    personalize: group.personalize,
  })
  const [error, setError] = useState<string | null>(null)
  const [samples, setSamples] = useState<{ id: number; name: string; body: string }[] | null>(null)
  const save = useCreatorMutation(() => api.updateOutreachGroup(outreachId, group.id, { label: label.trim() || group.label, ...v, offer: v.offer.trim() }))
  const rewrite = useCreatorMutation(() => api.generateOutreach(outreachId, { group_id: group.id, rewrite: true }))
  const sample = useCreatorMutation(() => api.sampleOutreachGroup(outreachId, group.id))

  const dirty =
    label !== group.label ||
    v.offer !== group.offer ||
    v.angle !== group.angle ||
    v.tone !== group.tone ||
    v.template !== group.template ||
    v.personalize !== group.personalize
  const check = checkTemplate(v.template, language, platform)
  const bad = !check.ok || !v.offer.trim()

  const saveIfDirty = async () => {
    if (dirty) await save.mutateAsync()
  }

  return (
    <Drawer
      open
      onClose={onClose}
      title={`${group.label} 그룹 설정`}
      footer={
        <>
          <Button onClick={onClose}>닫기</Button>
          <Button
            loading={sample.isPending}
            disabled={bad}
            onClick={async () => {
              setError(null)
              try {
                await saveIfDirty()
                const res = await sample.mutateAsync()
                setSamples(res.messages.map((m) => ({ id: m.creator.id, name: m.creator.display_name || `@${m.creator.handle}`, body: m.body })))
              } catch (e) {
                setError(errorText(e))
              }
            }}
          >
            예시 보기
          </Button>
          <Button
            loading={rewrite.isPending}
            disabled={bad}
            onClick={async () => {
              setError(null)
              try {
                await saveIfDirty()
                const r = await rewrite.mutateAsync()
                toast.success(`${r.started}명의 초안을 다시 쓴다`)
                onClose()
              } catch (e) {
                setError(errorText(e))
              }
            }}
          >
            다시 쓰기
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!dirty || bad}
            onClick={async () => {
              setError(null)
              try {
                await save.mutateAsync()
                toast.success('그룹 설정을 저장했다')
              } catch (e) {
                setError(errorText(e))
              }
            }}
          >
            저장
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="그룹 이름">
          <Input value={label} onChange={(e) => setLabel(e.target.value)} />
        </Field>
        <GroupFields idPrefix={group.label} value={v} onChange={(p) => setV((cur) => ({ ...cur, ...p }))} language={language} platform={platform} />
        <p className="text-meta text-muted-foreground">
          저장하면 새로 쓰는 초안부터 바뀐다. 이미 쓴 초안은 “다시 쓰기”를 눌러야 바뀐다. 승인했거나 보낸 것은 건드리지 않는다.
        </p>
        {error && (
          <p role="alert" className="break-words text-table text-status-failed">
            {error}
          </p>
        )}
        {samples && (
          <section aria-label="예시" className="space-y-2">
            <h3 className="text-table font-semibold">예시 (저장되지 않는다)</h3>
            {samples.length === 0 && <p className="text-table text-muted-foreground">이 그룹에 보여 줄 사람이 없다.</p>}
            {samples.map((m) => (
              <div key={m.id} className="rounded-md border border-border p-3">
                <p className="text-meta font-medium">{m.name}</p>
                <p className="mt-1 whitespace-pre-wrap break-words text-table">{m.body}</p>
              </div>
            ))}
          </section>
        )}
      </div>
    </Drawer>
  )
}
